import { emit } from './emit'
import { ensurePageViewId } from './pageViewId'

/** Page-viewed events that feed the session replay Slack channels (new storefront pages and
 *  the pharmacy transmission failure order page). Backend (Nile-Core) dedupes Slack posts per
 *  user within its suppression window; the Set here only guards against firing more than once
 *  per client-side page view (e.g. React strict-mode remounts), same pattern as
 *  landingPageLatency. */

export const SessionReplayPageViewedEvents = {
    HOMEPAGE: 'new_storefront_homepage_viewed',
    PDP: 'new_storefront_pdp_viewed',
    PHARMACY_DETAIL_PAGE: 'new_storefront_pharmacy_detail_page_viewed',
    TRANSMISSION_FAILURE_ORDER_PAGE: 'transmission_failure_order_page_viewed',
} as const

export type SessionReplayPageViewedEvent =
    (typeof SessionReplayPageViewedEvents)[keyof typeof SessionReplayPageViewedEvents]

const emittedForPageView = new Set<string>()

export function _resetSessionReplayPageViewedDedupForTests(): void {
    emittedForPageView.clear()
}

export function trackSessionReplayPageViewed(
    event: SessionReplayPageViewedEvent,
    properties: Record<string, unknown> = {},
    // Callers that resolve async before firing (e.g. an RTK Query onQueryStarted awaiting
    // queryFulfilled) must capture this via ensurePageViewId() at the start of that async work
    // and pass it here — reading it fresh at resolution time would pick up whatever URL is
    // current then, not the one the sku_id/properties actually describe.
    pageViewIdOverride?: string,
): void {
    const pageViewId = pageViewIdOverride ?? ensurePageViewId()
    const dedupeKey = `${pageViewId}:${event}`
    if (emittedForPageView.has(dedupeKey)) return
    emittedForPageView.add(dedupeKey)

    emit(event, { page_view_id: pageViewId, ...properties })
}
